// -----------------------------------------------------------------------------
// Dashboard + Documents listing — DTOs and fetchers (Phase 5 Wave 5,
// agent-ui-dashboard).
//
// Local to this feature, mirroring src/DocuSense.Application/Dtos/Documents/*.cs.
// We deliberately do NOT modify src/api/types.ts — when these shapes stabilise
// the root may lift them into the shared module.
// -----------------------------------------------------------------------------

import { apiFetch } from '../../api/client';
import { Endpoints } from '../../api/types';

// ---------- Status enum (snake_case, matches DocumentStatus) ----------

export const DocumentStatuses = {
  Queued: 'queued',
  Processing: 'processing',
  Approved: 'approved',
  ReviewRequired: 'review_required',
  Rejected: 'rejected',
  Failed: 'failed',
  Cancelled: 'cancelled',
  /** Terminal container: the upload was split into N independent child documents. */
  Split: 'split',
} as const;
export type DocumentStatus = (typeof DocumentStatuses)[keyof typeof DocumentStatuses];

export const DOCUMENT_STATUS_OPTIONS: ReadonlyArray<{ value: DocumentStatus; label: string }> = [
  { value: 'queued', label: 'En cola' },
  { value: 'processing', label: 'Procesando' },
  { value: 'approved', label: 'Aprobado' },
  { value: 'review_required', label: 'Requiere revisión' },
  { value: 'rejected', label: 'Rechazado' },
  { value: 'failed', label: 'Fallido' },
  { value: 'cancelled', label: 'Cancelado' },
  { value: 'split', label: 'Dividido' },
];

/** Terminal statuses (used for charts that compute approval rates). */
export const TERMINAL_STATUSES: ReadonlyArray<DocumentStatus> = [
  'approved',
  'review_required',
  'rejected',
  'failed',
  'cancelled',
  'split',
];

// ---------- DTOs (mirror DocuSense.Application.Dtos.Documents) ----------

export interface DocumentSummaryResponse {
  jobId: string;
  status: DocumentStatus | string;
  documentTypeSlug: string;
  documentTypeVersion: string;
  submittedAt: string; // ISO 8601
  completedAt?: string | null;
  globalScore?: number | null;
  /**
   * True once the retention window elapsed and the document expired: the stored file was deleted and the
   * extracted values scrubbed. `status` still carries the original outcome, so render this as an extra
   * "caducado" badge rather than replacing the status.
   */
  expired?: boolean;
  /** Reviewer user id this job is assigned to (J1); null when unassigned. */
  assignedTo?: string | null;
}

export interface PagedDocumentJobsResponse {
  items: DocumentSummaryResponse[];
  page: number;
  pageSize: number;
  totalCount: number;
}

export interface ListDocumentsQuery {
  status?: DocumentStatus | undefined;
  documentTypeSlug?: string | undefined;
  from?: string | undefined; // ISO 8601 (date or datetime)
  to?: string | undefined;
  page?: number | undefined;
  pageSize?: number | undefined;
  /** Reviewer user id filter (J1): only jobs assigned to this user. */
  assigneeId?: string | undefined;
  /** Client-side only; not sent to the API. */
  minGlobalScore?: number | undefined;
  /** Client-side only; not sent to the API. */
  maxGlobalScore?: number | undefined;
}

// ---------- Fetchers ----------

function buildQueryString(query: ListDocumentsQuery): string {
  const params = new URLSearchParams();
  if (query.status !== undefined && (query.status as string) !== '') {
    params.set('status', query.status);
  }
  if (query.documentTypeSlug !== undefined && query.documentTypeSlug !== '') {
    params.set('documentTypeSlug', query.documentTypeSlug);
  }
  if (query.from !== undefined && query.from !== '') {
    params.set('from', query.from);
  }
  if (query.to !== undefined && query.to !== '') {
    params.set('to', query.to);
  }
  if (query.page !== undefined) {
    params.set('page', String(query.page));
  }
  if (query.pageSize !== undefined) {
    params.set('pageSize', String(query.pageSize));
  }
  if (query.assigneeId !== undefined && query.assigneeId !== '') {
    params.set('assigneeId', query.assigneeId);
  }
  const qs = params.toString();
  return qs === '' ? '' : `?${qs}`;
}

export function listDocuments(
  query: ListDocumentsQuery,
  signal?: AbortSignal,
): Promise<PagedDocumentJobsResponse> {
  const path = `${Endpoints.documents}${buildQueryString(query)}`;
  return apiFetch<PagedDocumentJobsResponse>(path, signal !== undefined ? { signal } : {});
}

// ---------- Upload ----------

/** Mirrors DocuSense.Application.Dtos.Documents.SubmitDocumentResponse. */
export interface SubmitDocumentResponse {
  jobId: string;
  status: DocumentStatus | string;
  statusUrl: string;
}

export interface UploadDocumentRequest {
  file: File;
  documentTypeSlug: string;
}

/**
 * Sentinel `documentTypeSlug` for upload/batch meaning "detect the type automatically". Mirrors
 * `Tenant.AutoClassificationSentinel` on the backend: sending it (or omitting the slug entirely) routes
 * the upload through auto-classification. Only offered to tenants entitled via `features.autoClassificationEnabled`.
 */
export const AUTO_CLASSIFY_SLUG = '__auto__' as const;

/** 20 MB ceiling — mirrors DocumentsEndpoints.MaxUploadSizeBytes server-side. */
export const MAX_UPLOAD_SIZE_BYTES = 20 * 1024 * 1024;

/**
 * Submits a document for asynchronous processing. The endpoint accepts
 * multipart/form-data with the binary in field 'file' and the target schema in
 * 'documentTypeSlug'. apiFetch picks up the Authorization header from TokenStore,
 * and the browser sets the Content-Type boundary automatically — do NOT set it
 * manually or the boundary will be missing.
 */
export function uploadDocument(
  request: UploadDocumentRequest,
): Promise<SubmitDocumentResponse> {
  const form = new FormData();
  form.append('file', request.file, request.file.name);
  form.append('documentTypeSlug', request.documentTypeSlug);

  return apiFetch<SubmitDocumentResponse>(`${Endpoints.documents}/upload`, {
    method: 'POST',
    body: form,
  });
}

// ---------- Batches (F) ----------

export interface BatchFileFailure {
  fileName: string;
  error: string;
}

export interface CreateBatchResponse {
  batchId: string | null;
  documentTypeSlug: string;
  totalFiles: number;
  jobIds: string[];
  failures: BatchFileFailure[];
}

export interface BatchSummaryResponse {
  batchId: string;
  documentTypeSlug: string;
  createdAt: string;
  totalFiles: number;
}

export interface BatchJobStatus {
  jobId: string;
  status: string;
}

export interface BatchStatusResponse {
  batchId: string;
  documentTypeSlug: string;
  createdAt: string;
  total: number;
  statusCounts: Record<string, number>;
  jobs: BatchJobStatus[];
}

/** Max files per batch — mirrors CreateBatchUseCase.MaxFilesPerBatch server-side. */
export const MAX_BATCH_FILES = 50;

/** Submits multiple documents in one call as a batch (one job per file). */
export function createBatch(documentTypeSlug: string, files: File[]): Promise<CreateBatchResponse> {
  const form = new FormData();
  for (const file of files) {
    form.append('files', file, file.name);
  }
  form.append('documentTypeSlug', documentTypeSlug);
  return apiFetch<CreateBatchResponse>(`${Endpoints.documents}/batches`, { method: 'POST', body: form });
}

/** Lists the tenant's recent batches. */
export function listBatches(signal?: AbortSignal): Promise<BatchSummaryResponse[]> {
  return apiFetch<BatchSummaryResponse[]>(`${Endpoints.documents}/batches`, signal !== undefined ? { signal } : {});
}

/** Returns the aggregate status of a batch and its jobs. */
export function getBatchStatus(batchId: string, signal?: AbortSignal): Promise<BatchStatusResponse> {
  return apiFetch<BatchStatusResponse>(
    `${Endpoints.documents}/batches/${encodeURIComponent(batchId)}`,
    signal !== undefined ? { signal } : {},
  );
}

// ---------- Reprocess (Wave 6.D) ----------

/** Optional body for {@link reprocessDocument}. Mirrors the backend `ReprocessDocumentRequest`. */
export interface ReprocessDocumentBody {
  /**
   * Target document-type slug. When supplied, the new job binds to that type — the reviewer
   * correcting a mis-classified type, or assigning a type to a job whose classification was left
   * unresolved. Absent/blank rebinds to the original job's own type.
   */
  newDocumentTypeSlug?: string;
}

/**
 * Re-queues the same document blob as a new independent job. Without a body the new job rebinds to the
 * original's own document type (snapshotted against its latest version); with `newDocumentTypeSlug` it
 * binds to that type instead — the correction path for a mis-classified or unresolved auto-classified
 * job. The original job is left intact — the new job's audit trail references it via
 * `reprocess_of:{originalJobId}`. Mirrors `POST /api/v1/documents/{id}/reprocess`. The endpoint enforces
 * the same monthly document cap as `/upload`, so a 402 surfaces just like for a fresh submission.
 */
export function reprocessDocument(
  jobId: string,
  body?: ReprocessDocumentBody,
): Promise<SubmitDocumentResponse> {
  const hasSlug = body?.newDocumentTypeSlug !== undefined && body.newDocumentTypeSlug !== '';
  return apiFetch<SubmitDocumentResponse>(
    `${Endpoints.documents}/${encodeURIComponent(jobId)}/reprocess`,
    hasSlug
      ? { method: 'POST', json: { newDocumentTypeSlug: body!.newDocumentTypeSlug } }
      : { method: 'POST' },
  );
}

/**
 * Requests cancellation of an in-flight job. Backend returns 202 Accepted — the actual orchestrator
 * termination happens out-of-band in Functions and the job moves to `cancelled` once persisted.
 * Mirrors `POST /api/v1/documents/{id}/cancel`. Only legal while `queued`/`processing`; the API
 * surfaces 422 for terminal states or missing orchestration id.
 */
export function cancelDocument(jobId: string): Promise<void> {
  return apiFetch<void>(
    `${Endpoints.documents}/${encodeURIComponent(jobId)}/cancel`,
    { method: 'POST' },
  );
}

// ---------- Metrics (Wave 9.A — GET /metrics) ----------

/** Mirrors `DailyDocumentCount` on the API. `date` is `yyyy-MM-dd` UTC. */
export interface DailyDocumentCount {
  date: string;
  count: number;
}

/** Mirrors `CorrectedFieldCount` on the API. */
export interface CorrectedFieldCount {
  fieldName: string;
  correctionCount: number;
}

/** Mirrors `TenantMetricsResponse` on the API. */
export interface TenantMetricsResponse {
  statusCounts: Record<string, number>;
  totalTerminal: number;
  totalSubmitted30d: number;
  perDay: DailyDocumentCount[];
  averageLatencyMs: number | null;
  latencySampleSize: number;
  topCorrectedFields: CorrectedFieldCount[];
}

/**
 * Fetches the tenant's 30-day metrics aggregated server-side. Replaces the legacy
 * `useJobsForMetrics` client-side aggregation that paged through 200 jobs.
 */
export function getTenantMetrics(signal?: AbortSignal): Promise<TenantMetricsResponse> {
  return apiFetch<TenantMetricsResponse>('/api/v1/metrics', signal !== undefined ? { signal } : {});
}

// ---------- ROI metrics (Phase C — GET /metrics/roi) ----------
//
// Customer-facing value view. Every figure here is derived from the tenant's own
// outcomes + a platform baseline — it deliberately carries NO internal cost.

/** Mirrors `RoiTrend` on the API. Delta ratios are null when the prior period was zero. */
export interface RoiTrend {
  previousHoursSaved: number;
  previousSavingsAmount: number;
  previousAutomationRate: number;
  hoursSavedDeltaRatio: number | null;
  savingsDeltaRatio: number | null;
  automationRateDelta: number;
}

/** Mirrors `RoiDocumentTypeQuality` on the API. */
export interface RoiDocumentTypeQuality {
  slug: string;
  name: string;
  documentsProcessed: number;
  automationRate: number;
  correctionsPerDoc: number;
}

/** Mirrors `RoiBaseline` on the API — the assumptions behind the savings figures. */
export interface RoiBaseline {
  manualMinutesPerDoc: number;
  laborRatePerHour: number;
  currency: string;
}

/** Mirrors `TenantRoiMetricsResponse` on the API. */
export interface TenantRoiMetricsResponse {
  periodDays: number;
  currency: string;
  documentsProcessed: number;
  autoApprovedCount: number;
  reviewedCount: number;
  failedCount: number;
  hoursSaved: number;
  savingsAmount: number;
  automationRate: number;
  correctionsPerDoc: number;
  subscriptionMonthlyCost: number | null;
  roiMultiple: number | null;
  averageLatencyMs: number | null;
  latencySampleSize: number;
  trend: RoiTrend;
  qualityByDocumentType: RoiDocumentTypeQuality[];
  baseline: RoiBaseline;
}

/** Fetches the tenant's 30-day ROI view aggregated server-side. */
export function getTenantRoiMetrics(signal?: AbortSignal): Promise<TenantRoiMetricsResponse> {
  return apiFetch<TenantRoiMetricsResponse>(
    '/api/v1/metrics/roi',
    signal !== undefined ? { signal } : {},
  );
}

// ---------- Onboarding status (Phase E1 — GET /onboarding/status) ----------

/** Mirrors `OnboardingStatusResponse` on the API. */
export interface OnboardingStatusResponse {
  hasProvider: boolean;
  hasDocumentType: boolean;
  hasProcessedDocument: boolean;
}

/** Fetches the tenant's onboarding readiness (provider / document type / processed document). */
export function getOnboardingStatus(signal?: AbortSignal): Promise<OnboardingStatusResponse> {
  return apiFetch<OnboardingStatusResponse>(
    '/api/v1/onboarding/status',
    signal !== undefined ? { signal } : {},
  );
}

// ---------- Query keys ----------

export const DashboardQueryKeys = {
  all: ['documents'] as const,
  list: (query: ListDocumentsQuery) =>
    [
      ...DashboardQueryKeys.all,
      'list',
      query.status ?? null,
      query.documentTypeSlug ?? null,
      query.from ?? null,
      query.to ?? null,
      query.page ?? 1,
      query.pageSize ?? 20,
      query.minGlobalScore ?? null,
      query.maxGlobalScore ?? null,
      query.assigneeId ?? null,
    ] as const,
  metrics: () => [...DashboardQueryKeys.all, 'metrics-bucket'] as const,
  roi: () => [...DashboardQueryKeys.all, 'roi-bucket'] as const,
  onboarding: () => [...DashboardQueryKeys.all, 'onboarding'] as const,
  batches: () => [...DashboardQueryKeys.all, 'batches'] as const,
  batchStatus: (batchId: string) => [...DashboardQueryKeys.all, 'batch', batchId] as const,
  license: ['license', 'status'] as const,
};
